import { ref, computed, onBeforeUnmount } from 'vue'
import type {
  SkeletonPointByPointManager,
  SkeletonProgressData,
} from '@/modules/Editor/plugins/skeleton/SkeletonPointByPointManager'
// eslint-disable-next-line boundaries/element-types
import { useEditorV2 } from '@/composables/useEditorV2/useEditorV2'

const globalProgressData = ref<SkeletonProgressData | null>(null)
const globalManager = ref<SkeletonPointByPointManager | null>(null)

interface SuccessState {
  isVisible: boolean
  savedNodesCount: number
  timestamp: number
}

const globalSuccessState = ref<SuccessState>({
  isVisible: false,
  savedNodesCount: 0,
  timestamp: 0,
})

export const useSkeletonToolState = () => {
  const { editor } = useEditorV2()
  const isActive = computed(() => globalProgressData.value !== null)
  const isComplete = computed(() => globalProgressData.value?.isComplete ?? false)
  const hasProgress = computed(() => (globalProgressData.value?.placedNodesCount ?? 0) > 0)
  const isWaitingForFirstNode = computed(() => isActive.value && !hasProgress.value)
  const isInProgress = computed(() => hasProgress.value && !isComplete.value)
  const isReadyToSave = computed(() => isComplete.value)

  const cleanup = (): void => {
    if (globalManager.value) {
      globalManager.value.removeAllListeners()
      globalManager.value = null
    }
    globalProgressData.value = null
  }

  const initializeManager = (skeletonManager: SkeletonPointByPointManager): void => {
    cleanup()

    globalManager.value = skeletonManager

    const onProgress = (data: SkeletonProgressData) => {
      globalProgressData.value = data
    }

    skeletonManager.on('progress', onProgress)

    const initialProgress = skeletonManager.getProgressData()
    globalProgressData.value = initialProgress
  }

  const reset = (): void => {
    if (globalManager.value) {
      globalManager.value.reset()
    }
    globalProgressData.value = null
  }

  const undoLastNode = (): boolean => {
    if (!globalManager.value) return false
    return globalManager.value.removeLastNode()
  }

  const toggleNextNodeObscured = (): boolean => {
    if (!globalManager.value) return false
    return globalManager.value.toggleNextNodeObscured()
  }

  const restoreAllObscured = (): void => {
    if (!globalManager.value) return
    globalManager.value.restoreAllObscuredNodes()

    if (editor.value) {
      const currentTool = editor.value.toolManager.currentTool
      if (currentTool?.tool.draw) {
        currentTool.tool.draw(editor.value.activeView)
      }
    }
  }

  const canUndo = computed(() => (globalProgressData.value?.placedNodesCount ?? 0) > 0)

  const isShowingSuccess = computed(() => globalSuccessState.value.isVisible)
  const successState = computed(() => globalSuccessState.value)

  const showSuccessFeedback = (savedNodesCount: number): void => {
    globalSuccessState.value = {
      isVisible: true,
      savedNodesCount,
      timestamp: Date.now(),
    }

    setTimeout(() => {
      globalSuccessState.value = {
        ...globalSuccessState.value,
        isVisible: false,
      }
    }, 1000)
  }

  const hideSuccessFeedback = (): void => {
    globalSuccessState.value = {
      ...globalSuccessState.value,
      isVisible: false,
    }
  }

  onBeforeUnmount(() => {
    cleanup()
  })

  return {
    progressData: globalProgressData,
    isActive,
    isComplete,
    hasProgress,
    isWaitingForFirstNode,
    isInProgress,
    isReadyToSave,
    canUndo,
    isShowingSuccess,
    successState,
    showSuccessFeedback,
    hideSuccessFeedback,
    initializeManager,
    cleanup,
    reset,
    undoLastNode,
    toggleNextNodeObscured,
    restoreAllObscured,
  }
}
